// data/content-loader.js — fetch helpers for the content tree.
//
// Mirrors `Assets/_App/Engines/ContentLoader.cs` from the Unity port, but with
// `fetch` instead of `UnityWebRequest` and `await` instead of coroutines.
//
// The content tree:
//   content/content_index.json     master directory of all entries
//   content/items/<id>.json        per-item descriptor
//   content/lists/<id>.json        list with [{id, requiredPlays}]
//   content/lyrics/<id>_lyrics.json  [{time, zh, en}]
//
// `loadIndex()` is called once at boot and caches the index in memory.
// `loadItem`, `loadList`, `loadLyrics` are lazy and per-request.

/** @typedef {import("../types").RMItem} RMItem */
/** @typedef {import("../types").RMItemType} RMItemType */
/** @typedef {import("../types").RMListNode} RMListNode */
/** @typedef {import("../types").LyricLine} LyricLine */

/**
 * @typedef {object} IndexEntry
 * @property {string} id
 * @property {"item"|"list"|"lyrics"} kind
 * @property {RMItemType} [type]                only on `item` entries
 * @property {string} [title]
 * @property {string} [media]
 * @property {string} file
 */

// CONTENT_BASE is injected at build time via esbuild's --define (see
// build.mjs). In dev (no define), falls back to "/content" so the dev
// server serves from the project root. In production, esbuild substitutes
// the production CDN URL (e.g. "https://cdn.rhythmicapp.com/mandarin").
//
// Absolute path so fetches resolve correctly from any SPA route (e.g.
// /list/root, /play/RM0101PY) instead of being interpreted relative to the
// current document URL.
const CONTENT_BASE =
  // eslint-disable-next-line no-undef
  typeof __CONTENT_BASE__ !== "undefined" ? __CONTENT_BASE__ : "/content";

/** @type {Map<string, IndexEntry>} */ const index = new Map();
/** @type {Map<string, RMItem>} */     const itemCache = new Map();
/** @type {Map<string, RMListNode>} */ const listCache = new Map();
/** @type {Map<string, LyricLine[]>} */ const lyricsCache = new Map();
/** @type {Set<string>} */              const hasLyricsSet = new Set();

/** @type {Promise<void> | null} */
let indexPromise = null;

/**
 * Load `content_index.json` and populate the index/hasLyrics caches.
 * Idempotent and concurrent-safe — multiple callers share the same in-flight
 * fetch so `<rm-app-shell>`'s synchronous mount can race `bootstrap()` without
 * double-fetching the index.
 */
export function loadIndex() {
  if (indexPromise) return indexPromise;
  indexPromise = (async () => {
    const res = await fetch(`${CONTENT_BASE}/content_index.json`);
    if (!res.ok) throw new Error(`[content-loader] index ${res.status}`);
    const data = /** @type {{entries: IndexEntry[]}} */ (await res.json());
    for (const e of data.entries) {
      index.set(e.id, e);
      if (e.kind === "lyrics") {
        // lyric files are stored as `<itemId>_lyrics`; mark the parent item
        const itemId = e.id.replace(/_lyrics$/, "");
        hasLyricsSet.add(itemId);
      }
    }
    console.log(`[content-loader] index loaded — ${index.size} entries, ${hasLyricsSet.size} items have lyrics`);
  })();
  return indexPromise;
}

/**
 * @param {string} id
 * @returns {Promise<RMItem|null>}
 */
export async function loadItem(id) {
  await loadIndex();
  const cached = itemCache.get(id);
  if (cached) return cached;
  const entry = index.get(id);
  if (!entry || entry.kind !== "item") return null;
  const res = await fetch(`${CONTENT_BASE}/${entry.file}`);
  if (!res.ok) return null;
  const raw = /** @type {any} */ (await res.json());
  /** @type {RMItem} */
  const item = {
    id: raw.id,
    type: raw.type,
    title: raw.title ?? "",
    titleLine2: raw.title_line2 ?? "",
    description: raw.description ?? "",
    titleZh: raw.title_zh,
    titleLine2Zh: raw.title_line2_zh,
    descriptionZh: raw.description_zh,
    mediaFilename: raw.media ?? "",
    durationSec: raw.duration_sec ?? 0,
    companionAudioId: raw.companion_audio_id ?? null,
    hasLyrics: hasLyricsSet.has(id),
  };
  itemCache.set(id, item);
  return item;
}

/**
 * @param {string} id
 * @returns {Promise<RMListNode|null>}
 */
export async function loadList(id) {
  await loadIndex();
  const cached = listCache.get(id);
  if (cached) return cached;
  const entry = index.get(id);
  if (!entry || entry.kind !== "list") return null;
  const res = await fetch(`${CONTENT_BASE}/${entry.file}`);
  if (!res.ok) return null;
  const raw = /** @type {any} */ (await res.json());
  /** @type {RMListNode} */
  const list = {
    id: raw.id,
    title: raw.title ?? "",
    titleLine2: raw.title_line2 ?? "",
    items: (raw.items ?? []).map(/** @param {any} c */ (c) => ({
      id: c.id,
      requiredPlays: c.required_plays ?? 1,
    })),
  };
  listCache.set(id, list);
  return list;
}

/**
 * @param {string} itemId
 * @returns {Promise<LyricLine[]>} empty array if no lyrics file
 */
export async function loadLyrics(itemId) {
  await loadIndex();
  const cached = lyricsCache.get(itemId);
  if (cached) return cached;
  if (!hasLyricsSet.has(itemId)) return [];
  const lyricsId = `${itemId}_lyrics`;
  const entry = index.get(lyricsId);
  if (!entry) return [];
  const res = await fetch(`${CONTENT_BASE}/${entry.file}`);
  if (!res.ok) return [];
  const raw = /** @type {any} */ (await res.json());
  /** @type {LyricLine[]} */
  const lines = (raw.lines ?? []).map(/** @param {any} l */ (l) => ({
    timeSec: l.time ?? 0,
    zh: l.zh ?? "",
    en: l.en ?? "",
  }));
  lyricsCache.set(itemId, lines);
  return lines;
}

/** @returns {Iterable<IndexEntry>} */
export function entries() { return index.values(); }

/** @param {string} id */
export function entry(id) { return index.get(id); }
